import Vue from 'vue'
import Vuex from 'vuex'
import {
	userTypeMap,
	defaultIconColors,
	iconSizeBig,
	buildContentArray,
	iconFontMap,
	fontSizeMap,
	textBoxHeight,
	iconSizeMap,
	textSizeMap,
	mapLayout,
	styleMap,
	defaultUserFileTypePrivilegies,
	defaultUserPagePrivilegies,
	productCode,
	numberOfIconsPerLayout
} from './storeConstants';

Vue.use(Vuex)
const store = new Vuex.Store({
	state: {
		downloaderBroadcastChannel: process.env.VUE_APP_MODE + '_downloader_channel',
		downloaderBrodcastId: Math.floor(Math.random() * 1000),
		dxfServerURL: process.env.VUE_APP_DXF_SERVER_URL,
		serverURL: process.env.VUE_APP_SERVER_URL,
		user: null,
		artwork: {},
		newArtworkToProductId: null,
		editedIdArtworkTool: null,
		bigIconSize: iconSizeBig.label, //double and slider icons are always big
		fontSizeMap,
		textBoxHeight,
		iconSizeMap,
		textSizeMap,
		defaultUserFileTypePrivilegies,
		defaultUserPagePrivilegies,
		iconFontMap,
		mapLayout,
		styleMap,
		productCode,
		userTypeMap
	},
	getters: {
		getDefaultIconColor: () => (color) => {
			return defaultIconColors[color] || '#ffffff';
		},
		downloaderBroadcastChannel: (state) => (id = null) => {
			if (id == null) {
				id = state.downloaderBrodcastId;
			}
			return state.downloaderBroadcastChannel + '_' + id;
		},
		divider(state) { //style layout divider
			var format = {
				single: 1,
				double: 2,
				vdouble: 1,
				triple: 3,
				vtriple: 1,
			};
			return format[state.artwork.format];
		},
		canDownloadFile: (state) => (type) => {
			if (state.user != null && typeof state.user.checkedPagePermissions !== 'undefined') {
				return state.user.checkedFilePermissions.includes(type);
			}
			return true;
		},
		canSeePage: (state) => (page) => {
			if (state.user != null && typeof state.user.checkedPagePermissions !== 'undefined') {
				return state.user.checkedPagePermissions.includes(page);
			}
			return true;
		},
		isVertical(state) {
			return state.artwork?.format?.substring(0, 1).includes('v')
		},

		isDouble(state) {
			return state.artwork?.format?.includes('double');
		},

		isTriple(state) {
			return state.artwork?.format?.includes('triple');
		},
		hOrV(state, getters) {
			var hOrV = getters.isVertical ? "vertical" : "horizontal";
			hOrV = getters.isTriple ? hOrV + "  triple" : hOrV;

			return hOrV;
		},
		isFullMetal(state) {
			if ((state.artwork.type == 'blackjack' || state.artwork.type == 'alba') && state.artwork.keyColor.startsWith('m')) {
				return true;
			}
			return false;
		},
		metalOrDefault(state, getters) {
			return getters.isFullMetal
				? 'metal'
				: 'default';
		},
		squareOrRectangle(state) {
			if (state.artwork.buttonFormat === "round") {
				return 'square';
			} else if (state.artwork.buttonFormat === "oval") {
				return 'rectangle';
			}
			return state.artwork.buttonFormat;
		},
		isFrameOfStone(state) {
			return state.artwork.frameColor.startsWith('s');
		},
		getGridPosition: (state) => ( index, layoutKey = 'leftSide') =>  {
			if (state.artwork.type == "alba" || state.artwork.type == "blackjack") {
				let leftButton = state.artwork[layoutKey + 'LeftButton'];

				let numOfIconsLeftButton = numberOfIconsPerLayout(store)?.[state.artwork.type]?.[leftButton] || 0;

				index = index + 1; //index starts from 0, but we need to start from 1 for gridRow

				if (numOfIconsLeftButton == 0) {
					return {};
				}

				if (index <= numOfIconsLeftButton) {
				return { gridRow: index, gridColumn: 1 }
				} else {
				return { gridRow: index - numOfIconsLeftButton, gridColumn: 2 };
				}
			} else {
				return { gridRow: Math.floor(index / 3) + 1, gridColumn: (index % 3) + 1 }
			}
		},
	},

	mutations: {
		changeFontColor(state, color) {
			for (var i = 0; i < state.artwork.content.length; i++) {
				state.artwork.content[i].color = color
			}
			if (typeof state.artwork.rightContent != 'undefined') {
				for (i = 0; i < state.artwork.rightContent.length; i++) {
					state.artwork.rightContent[i].color = color
				}
			}
			if (typeof state.artwork.tripleRightContent != 'undefined') {
				for (i = 0; i < state.artwork.tripleRightContent.length; i++) {
					state.artwork.tripleRightContent[i].color = color
				}
			}
		},
		fullMetalSettings(state) {
			state.artwork.font = "blacknovametal"
			state.artwork.fontType = "Black Nova Metal"
			state.artwork.iconSize = 8
			state.artwork.rightIconSize = 8
			state.artwork.tripleRightIconSize = 8
			state.artwork.fontSize = 3.5
			state.artwork.rightFontSize = 3.5
			state.artwork.tripleRightFontSize = 3.5
			this.commit("getContent")
			this.commit("getTripleRightContent")
			this.commit("getRightContent")

		},
		standardSettings(state) {
			state.artwork.font = "avenirblacknova"
			state.artwork.fontType = "Avenir Black Nova"
			state.artwork.iconSize = 8
			state.artwork.rightIconSize = 8
			state.artwork.tripleRightIconSize = 8
			state.artwork.fontSize = 3.5
			state.artwork.rightFontSize = 3.5
			state.artwork.tripleRightFontSize = 3.5
			this.commit("getContent")
			this.commit("getTripleRightContent")
			this.commit("getRightContent")
		},
		setAlignment(state, payload) {
			if (typeof state.artwork.content[payload.index] != 'undefined') {
				state.artwork.content[payload.index].alignment = payload.alignment;
			}
		},
		setRightAlignment(state, payload) {
			if (typeof state.artwork.rightContent[payload.index] != 'undefined') {
				state.artwork.rightContent[payload.index].alignment = payload.alignment;
			}
		},
		setTripleRightAlignment(state, payload) {
			if (typeof state.artwork.tripleRightContent[payload.index] != 'undefined') {
				state.artwork.tripleRightContent[payload.index].alignment = payload.alignment;
			}
		},
		getContent(state) {
			state.artwork.content = buildContentArray(state, 'layout', 'layoutStyle', store);
		},
		getTripleRightContent(state) {
			state.artwork.tripleRightContent = buildContentArray(state, 'tripleRightLayout', 'tripleRightLayoutStyle', store);
		},
		getRightContent(state) {
			state.artwork.rightContent = buildContentArray(state, 'rightLayout', 'rightLayoutStyle', store);
		},

		getProductCode(state) {
			var pc = state.productCode;
			var layout = (pc.opt1[state.artwork.type][state.artwork.layout]) ? pc.opt1[state.artwork.type][state.artwork.layout] : state.artwork.layout;

			if (store.getters.isDouble || store.getters.isTriple) {
				layout = 'CO';
			}
			
			state.artwork.productCode = pc.category +
				pc.series[state.artwork.type] +
				layout +
				pc.opt2[state.artwork.format] +
				pc.opt3[state.artwork.protocol] +
				pc.opt4(state.artwork.frameColor) +
				pc.opt5(state.artwork) +
				pc.prn(state, store) +
				((state.artwork.type == 'blackjack') ? pc.opt6[state.artwork.buttonFormat] : '') +
				((store.getters.isVertical) ? 'V' : '') + 
				((state.artwork.productLine == 'Classic' && (state.artwork.layout == 'M1' || ((state.artwork.format == 'double' || state.artwork.format == 'vdouble') && state.artwork.rightLayout == 'M1'))) ? '-C' : '') 
					
		}
	},
	actions: {},
	modules: {}
});

export default store;